import { useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { haptic } from '../lib/sfx';
import { markPmAdShown } from '../lib/pmAd';
import { PM_QUESTION, pmCtaUrl, pmQuestionForToday } from '../lib/pmQuestion';
import { trackEvent } from '../utils/analytics';

// The eleventh question. Not a film: the game's own question screen asking
// the first item of the PsycheMatrix assessment, then the truth about it and
// a way in. Rules that keep it honest:
//   - "Back to the game" is on screen from the first frame (Google Play wants
//     an interstitial closable within 15 s; this one is closable at 0 s)
//   - no clock: the assessment does not time an answer, so no ring counts down
//   - no right answer is ever lit; the reveal says there is none
//   - the CTA carries the answer given here so the assessment can start from
//     item two once PsycheMatrix reads it (until then it starts from item one)

const ACCENT = '#c4973a';   // PsycheMatrix's amber: this screen is theirs, the realm colours stay the game's
const LETTERS = ['A', 'B', 'C', 'D', 'E'];

interface Props {
  onDone: () => void;
}

export function PmQuestion({ onDone }: Props) {
  const [item] = useState(() => pmQuestionForToday());
  const [picked, setPicked] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const scroll = useRef<HTMLDivElement>(null);

  useEffect(() => {
    markPmAdShown();
    trackEvent('ad_impression', { creative: PM_QUESTION.creative, format: PM_QUESTION.format, question: item.id });
  }, [item.id]);

  // the pick settles for a beat, then the reveal replaces the options
  useEffect(() => {
    if (!picked) return;
    const t = setTimeout(() => setRevealed(true), 650);
    return () => clearTimeout(t);
  }, [picked]);

  // the reveal starts at the top of the screen, whatever the list had scrolled to
  useEffect(() => {
    if (revealed) scroll.current?.scrollTo({ top: 0 });
  }, [revealed]);

  const pick = (optionId: string) => {
    if (picked) return;
    haptic('medium');
    setPicked(optionId);
    trackEvent('q11_answer', { creative: PM_QUESTION.creative, question: item.id, option: optionId });
  };

  const leave = () => {
    trackEvent('ad_skip', { creative: PM_QUESTION.creative, question: item.id, answered: picked !== null });
    onDone();
  };

  const openCta = async () => {
    const url = pmCtaUrl(picked ? { question: item.id, option: picked } : undefined);
    trackEvent('ad_cta_tap', { creative: PM_QUESTION.creative, target: 'assessment', question: item.id, option: picked });
    try {
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url, windowName: '_system' });
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch { /* nothing to do: the player is still in the game */ }
    onDone();
  };

  return (
    <div className={`ad-stage pmq${revealed ? ' pmq-pm' : ''}`} role="dialog" aria-label="PsycheMatrix question" style={{ ['--realm-accent' as never]: ACCENT }}>
      <div className="pmq-scroll" ref={scroll}>
        {/* the way out sits in the scoreboard row, where the round pill would be: visible from the first frame, never over anything.
            On the reveal the row becomes PsycheMatrix's: their lockup, rendered from their own components (public/pm),
            on their light surface; only the question set stays dark. */}
        <div className="scoreboard">
          {revealed ? (
            <img className="pmq-lockup" src={`${import.meta.env.BASE_URL}pm/pm-lockup-dark.png`} alt="PsycheMatrix" />
          ) : (
            <div className="scoreboard-player">
              <span className="scoreboard-name" style={{ color: ACCENT }}>PSYCHEMATRIX</span>
              <span className="scoreboard-score" style={{ color: '#ccccd4' }}>ASSESSMENT</span>
            </div>
          )}
          <button className="ad-skip ad-skip-ready pmq-skip" onClick={leave}>Back to the game</button>
        </div>

        <div className="game-content-wrapper pmq-content">
          {!revealed ? (
            <>
              <span className="realm-tag pmq-tag">ONE MORE QUESTION</span>
              <p className="question-text">{item.text}</p>
              <div className="answer-grid pmq-grid">
                {item.options.map((o, i) => (
                  <button
                    key={o.id}
                    className={`answer-card pmq-card ${picked === o.id ? 'answer-selected' : ''} ${picked && picked !== o.id ? 'answer-locked' : ''}`}
                    onClick={() => pick(o.id)}
                    disabled={picked !== null}
                  >
                    <span className="letter-badge">{LETTERS[i]}</span>
                    <span className="pmq-option">
                      <span className="pmq-option-label">{o.label}</span>
                      <span className="pmq-option-desc">{o.description}</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="pmq-reveal">
              {/* No reading of the answer, on purpose: PsycheMatrix's own rule is that a
                  single answer never carries an identity claim. One line says what just
                  happened, the product itself says the rest: the flow's real second
                  screen, rendered from the app's components (public/pm/at-screen-q2.jpg,
                  cropped to the question set: panel and all five options).
                  The way out is the header button; nothing is repeated below. */}
              <p className="pmq-reveal-text">That was question 1 of the PsycheMatrix assessment.</p>
              <div className="pmq-phone">
                <img src={`${import.meta.env.BASE_URL}pm/at-screen-q2.jpg`} alt="The PsycheMatrix assessment, question 2 of 56" />
                <div className="pmq-phone-fade" />
              </div>
              <p className="pmq-reveal-sub">
                56 questions, 5 short tasks, a 30 minute test.
                <br />
                <strong>Free to start.</strong> Your answer comes with you.
              </p>
              <button className="ad-cta-button pmq-cta" onClick={openCta}>Continue with question 2</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
